import { useState, useEffect } from 'react'
import { TrendingUp, TrendingDown, DollarSign, Percent, AlertCircle, RefreshCw, BarChart3, Clock, Shield } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { gestaoService } from '../../services/gestaoService'
import leiloesService from '../../services/leiloesService'
import toast from 'react-hot-toast'

const fmt = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

function StatCard({ label, value, icon: Icon, bg }) {
  return (
    <div className={`${bg} rounded-xl p-5 flex items-center gap-4 shadow-sm`}>
      <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-white/20">
        <Icon className="w-6 h-6 text-white" />
      </div>
      <div>
        <p className="text-white/80 text-xs">{label}</p>
        <p className="text-white text-lg font-bold">{value}</p>
      </div>
    </div>
  )
}

function BarChart({ data, color }) {
  if (!data?.length) return <p className="text-gray-400 text-sm text-center py-6">Sem dados</p>
  const max = Math.max(...data.map(d => d.total), 1)
  return (
    <div className="space-y-2 mt-2">
      {data.filter(d => d.total > 0).map((d) => (
        <div key={d.nome} className="flex items-center gap-2">
          <span className="text-xs text-gray-600 w-28 truncate flex-shrink-0">{d.nome}</span>
          <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
            <div className={`h-full rounded-full ${color} transition-all duration-500`}
              style={{ width: `${Math.max((d.total / max) * 100, 3)}%` }} />
          </div>
          <span className="text-xs font-semibold text-gray-700 w-24 text-right flex-shrink-0">{fmt(d.total)}</span>
        </div>
      ))}
    </div>
  )
}

export default function GestaoDashboard() {
  const { organizadorAtivo } = useOrganizador()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [leiloes, setLeiloes] = useState([])
  const [modoPeriodo, setModoPeriodo] = useState(false)
  const [leilaoId, setLeilaoId] = useState('')
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const orgId = organizadorAtivo?.id

  useEffect(() => {
    if (!orgId) return
    gestaoService.inicializar(orgId).catch(() => {})
    leiloesService.getByOrganizador(orgId).then(r => {
      const sorted = [...(r || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
      setLeiloes(sorted)
      if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
    }).catch(() => {})
  }, [orgId])

  useEffect(() => {
    if (!orgId || (!leilaoId && !modoPeriodo)) return
    const syncAndLoad = async () => {
      if (leilaoId) { try { await gestaoService.sincronizar(orgId, leilaoId) } catch {} }
      carregar()
    }
    syncAndLoad()
  }, [orgId, leilaoId])

  const carregar = async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const filtros = {}
      if (!modoPeriodo && leilaoId) filtros.leilao_id = leilaoId
      if (modoPeriodo && dataInicio) filtros.data_inicio = dataInicio
      if (modoPeriodo && dataFim) filtros.data_fim = dataFim
      const res = await gestaoService.dashboard(orgId, filtros)
      setData(res.data)
    } catch { toast.error('Erro ao carregar dashboard') }
    finally { setLoading(false) }
  }

  if (!orgId) return <p className="text-gray-500 p-8">Selecione um organizador.</p>

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold text-gray-800">Dashboard - Gestão Financeira</h1>

      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg overflow-hidden border border-gray-200">
          <button onClick={() => setModoPeriodo(false)}
            className={`px-4 py-2 text-sm font-medium ${!modoPeriodo ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            Por Leilão
          </button>
          <button onClick={() => setModoPeriodo(true)}
            className={`px-4 py-2 text-sm font-medium ${modoPeriodo ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            Por Período
          </button>
        </div>
        {!modoPeriodo ? (
          <select value={leilaoId} onChange={e => setLeilaoId(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm min-w-[260px]">
            <option value="">Todos os leilões</option>
            {leiloes.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
          </select>
        ) : (
          <div className="flex items-center gap-2">
            <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            <span className="text-gray-400">até</span>
            <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" />
          </div>
        )}
        <button onClick={carregar} className="ml-auto flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
          <RefreshCw className="w-4 h-4" /> Filtrar
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <>
          {/* Cards principais */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <StatCard label="Total Receitas" value={fmt(data?.total_receitas)} icon={TrendingUp} bg="bg-green-500" />
            <StatCard label="Total Despesas" value={fmt(data?.total_despesas)} icon={TrendingDown} bg="bg-red-500" />
            <StatCard label="Resultado" value={fmt(data?.resultado)} icon={DollarSign} bg="bg-purple-600" />
            <StatCard label="Margem de Lucro" value={`${data?.margem_lucro ?? 0}%`} icon={Percent} bg="bg-blue-600" />
          </div>

          {/* Resumo do Leilão */}
          {data?.resumo_leilao && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-blue-500" /> Resumo do Leilão</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="text-center"><p className="text-xs text-gray-500">Lotes</p><p className="text-lg font-bold text-gray-800">{data.resumo_leilao.total_lotes}</p></div>
                <div className="text-center"><p className="text-xs text-gray-500">Animais</p><p className="text-lg font-bold text-gray-800">{data.resumo_leilao.total_animais}</p></div>
                <div className="text-center"><p className="text-xs text-gray-500">Total Vendido</p><p className="text-lg font-bold text-gray-800">{fmt(data.resumo_leilao.total_vendido)}</p></div>
                <div className="text-center"><p className="text-xs text-gray-500">Comissões</p><p className="text-lg font-bold text-green-600">{fmt(data.resumo_leilao.total_comissoes)}</p></div>
                <div className="text-center"><p className="text-xs text-gray-500">Despesas</p><p className="text-lg font-bold text-red-500">{fmt(data.resumo_leilao.total_despesas_leilao)}</p></div>
                <div className="text-center"><p className="text-xs text-gray-500">Líquido</p><p className="text-lg font-bold text-purple-600">{fmt(data.resumo_leilao.liquido)}</p></div>
              </div>
            </div>
          )}

          {/* Composição + Indicadores */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-2">Composição — Receitas</h2>
              <BarChart data={data?.composicao_receitas} color="bg-green-500" />
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-2">Composição — Despesas</h2>
              <BarChart data={data?.composicao_despesas} color="bg-red-400" />
            </div>
          </div>

          {/* Contas a Receber / Pagar com valores */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-gray-700">Comissão a Receber</h2>
                <a href="/gestao/contas-receber" className="text-xs text-blue-600 hover:underline">Ver Todas →</a>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                  <p className="text-xs text-amber-600 mb-1">Em Aberto ({data?.contas_receber_em_aberto ?? 0})</p>
                  <p className="text-lg font-bold text-amber-700">{fmt(data?.cr_valor_aberto)}</p>
                </div>
                <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                  <div className="flex items-center gap-1 mb-1"><AlertCircle className="w-3 h-3 text-red-500" /><p className="text-xs text-red-500">Atrasadas ({data?.contas_receber_atrasadas ?? 0})</p></div>
                  <p className="text-lg font-bold text-red-600">{fmt(data?.cr_valor_atrasado)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-gray-700">Despesas do Leilão</h2>
                <a href="/gestao/contas-pagar" className="text-xs text-red-600 hover:underline">Ver Todas →</a>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                  <p className="text-xs text-amber-600 mb-1">Em Aberto ({data?.contas_pagar_em_aberto ?? 0})</p>
                  <p className="text-lg font-bold text-amber-700">{fmt(data?.cp_valor_aberto)}</p>
                </div>
                <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                  <div className="flex items-center gap-1 mb-1"><AlertCircle className="w-3 h-3 text-red-500" /><p className="text-xs text-red-500">Atrasadas ({data?.contas_pagar_atrasadas ?? 0})</p></div>
                  <p className="text-lg font-bold text-red-600">{fmt(data?.cp_valor_atrasado)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Últimas Movimentações + Indicadores */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Últimas Movimentações */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-gray-400" /> Últimas Movimentações</h2>
              {data?.ultimas_movimentacoes?.length > 0 ? (
                <div className="space-y-2">
                  {data.ultimas_movimentacoes.map((m, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${m.tipo === 'receita' ? 'bg-green-500' : 'bg-red-400'}`} />
                        <div>
                          <p className="text-sm text-gray-800 truncate max-w-[300px]">{m.descricao}</p>
                          <p className="text-xs text-gray-400">{m.data ? new Date(m.data + 'T00:00:00').toLocaleDateString('pt-BR') : ''}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-semibold ${m.tipo === 'receita' ? 'text-green-600' : 'text-red-500'}`}>{m.tipo === 'receita' ? '+' : '-'} {fmt(m.valor)}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded ${m.status === 'pago' ? 'bg-green-100 text-green-700' : m.status === 'atrasado' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{m.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <p className="text-gray-400 text-sm text-center py-6">Nenhuma movimentação</p>}
            </div>

            {/* Indicadores */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2"><Shield className="w-4 h-4 text-blue-500" /> Indicadores</h2>
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Taxa de Inadimplência</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                      <div className="h-full bg-red-400 rounded-full transition-all" style={{ width: `${Math.min(data?.taxa_inadimplencia || 0, 100)}%` }} />
                    </div>
                    <span className="text-sm font-bold text-gray-700">{data?.taxa_inadimplencia ?? 0}%</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Taxa de Recebimento</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                      <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${Math.min(100 - (data?.taxa_inadimplencia || 0), 100)}%` }} />
                    </div>
                    <span className="text-sm font-bold text-gray-700">{Math.max(100 - (data?.taxa_inadimplencia || 0), 0).toFixed(1)}%</span>
                  </div>
                </div>
                <div className="pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">Margem de Lucro</p>
                  <p className="text-2xl font-bold text-purple-600">{data?.margem_lucro ?? 0}%</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
